import {
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  Renderer2,
  inject,
  input,
} from '@angular/core';

import type { TooltipPosition, TooltipSeverity } from '@shared-models';

import { TooltipService } from '../../services/tooltip/tooltip.service';

// Design System mobile range (sm: ≤ 575px, see styles/utils/_breakpoints.scss).
const MOBILE_MEDIA_QUERY = '(max-width: 575px)';

@Directive({
  selector: '[appTooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private tooltipService = inject(TooltipService);

  appTooltip = input<string>('');
  tooltipPosition = input<TooltipPosition>('top');
  tooltipSeverity = input<TooltipSeverity>('info');
  tooltipDelay = input<number>(300);
  tooltipDisabled = input<boolean>(false);
  // Mobile-only host/tooltip distance (px); falls back to the service default.
  tooltipGapMobile = input<number | null>(null);

  private tooltipElement: HTMLElement | null = null;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private autoHideTimeout: ReturnType<typeof setTimeout> | null = null;
  private removeScrollListener: (() => void) | null = null;

  ngOnDestroy(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
    }
    this.hideTooltip();
  }

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(): void {
    if (this.tooltipDisabled() || !this.appTooltip().trim()) {
      return;
    }

    // Only works on desktop with real hover capability
    if (this.tooltipService.isDesktop()) {
      this.timeoutId = setTimeout(() => {
        this.showTooltip();
      }, this.tooltipDelay());
    }
  }

  @HostListener('mouseleave', ['$event'])
  onMouseLeave(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    // Only works on desktop with real hover capability
    if (this.tooltipService.isDesktop()) {
      this.hideTooltip();
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    if (this.tooltipDisabled() || !this.appTooltip().trim()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (this.tooltipElement) {
      this.hideTooltip();
    } else {
      this.showTooltip();
      // Auto-hide after 3 seconds on mobile/click
      this.autoHideTimeout = setTimeout(() => {
        this.hideTooltip();
      }, 3000);
    }
  }

  private showTooltip(): void {
    if (this.tooltipElement) {
      return;
    }

    try {
      // Create tooltip using service
      this.tooltipElement = this.tooltipService.createTooltip({
        hostElement: this.elementRef.nativeElement,
        message: this.appTooltip(),
        position: this.tooltipPosition(),
        severity: this.tooltipSeverity(),
        renderer: this.renderer,
        gap: this.resolveGap(),
      });

      // Add scroll listener for auto-hide
      this.removeScrollListener = this.tooltipService.addScrollListener(
        this.elementRef.nativeElement,
        () => this.hideTooltip(),
      );
    } catch (error) {
      // Handle tooltip creation errors gracefully
      console.warn('Failed to create tooltip:', error);
      this.tooltipElement = null;
      this.removeScrollListener = null;
    }
  }

  private resolveGap(): number | undefined {
    const mobileGap = this.tooltipGapMobile();
    if (mobileGap !== null && window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      return mobileGap;
    }

    const cssGap = parseFloat(
      getComputedStyle(this.elementRef.nativeElement).getPropertyValue(
        '--tooltip-gap',
      ),
    );
    return Number.isNaN(cssGap) ? undefined : cssGap;
  }

  private hideTooltip(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = null;
    }

    // Remove scroll listener
    if (this.removeScrollListener) {
      this.removeScrollListener();
      this.removeScrollListener = null;
    }

    if (this.tooltipElement) {
      this.tooltipService.hideTooltip(this.tooltipElement, this.renderer);
      this.tooltipElement = null;
    }
  }
}
